import { toRelativeUrl } from '@okta/okta-auth-js';
import { captureException } from '@sentry/react';

export const AUTH_REDIRECT_KEY = 'bennie-auth-redirect-in-progress';
const AUTH_REDIRECT_STARTED_AT_KEY = 'bennie-auth-redirect-started-at';
const REDIRECT_TIMEOUT_MS = 30_000;

let authRedirectStartedAt = null;

export function clearAuthRedirectSessionMarkers() {
  sessionStorage.removeItem(AUTH_REDIRECT_KEY);
  sessionStorage.removeItem(AUTH_REDIRECT_STARTED_AT_KEY);
}

export function resetAuthLoaderRedirectGuard() {
  authRedirectStartedAt = null;
}

function isSessionRedirectStale() {
  const flag = sessionStorage.getItem(AUTH_REDIRECT_KEY) === 'true';
  if (!flag) {
    return false;
  }
  const startedRaw = sessionStorage.getItem(AUTH_REDIRECT_STARTED_AT_KEY);
  const started = startedRaw ? Number(startedRaw) : NaN;
  if (!Number.isFinite(started)) {
    return true;
  }
  return Date.now() - started > REDIRECT_TIMEOUT_MS;
}

function clearStaleRedirectState() {
  authRedirectStartedAt = null;
  clearAuthRedirectSessionMarkers();
}

export const createAuthLoader = oktaAuth => async () => {
  if (!oktaAuth) {
    throw new Response(
      'Okta auth not initialized. Ensure oktaAuth is provided to createAuthLoader.',
      { status: 500, statusText: 'Auth Initialization Error' },
    );
  }

  // Loaders run when the data router matches (e.g. under RouterProvider), not when
  // createBrowserRouter() is called. Prefer storage-backed isAuthenticated() over
  // authStateManager.getAuthState() so we do not redirect before Security finishes.
  let isAuthenticated = false;
  try {
    isAuthenticated = await oktaAuth.isAuthenticated();
  } catch (error) {
    captureException(error, {
      tags: { section: 'authLoader', action: 'isAuthenticated' },
    });
    throw error;
  }

  if (isAuthenticated) {
    clearStaleRedirectState();
    const tokens = oktaAuth.tokenManager.getTokensSync();
    const accessToken = tokens?.accessToken;
    const idToken = tokens?.idToken;
    const userId = accessToken?.claims?.UserId;
    const userEmail = idToken?.claims?.email;
    const authState = {
      isAuthenticated: true,
      accessToken,
      idToken,
    };
    return {
      isAuthenticated: true,
      userId,
      userEmail,
      authState,
    };
  }

  if (sessionStorage.getItem(AUTH_REDIRECT_KEY) === 'true') {
    if (isSessionRedirectStale()) {
      clearStaleRedirectState();
    } else {
      return { isAuthenticated: false, isLoading: true };
    }
  }

  if (authRedirectStartedAt != null) {
    if (Date.now() - authRedirectStartedAt < REDIRECT_TIMEOUT_MS) {
      return { isAuthenticated: false, isLoading: true };
    }
    clearStaleRedirectState();
  }

  authRedirectStartedAt = Date.now();

  try {
    sessionStorage.setItem(AUTH_REDIRECT_KEY, 'true');
    sessionStorage.setItem(AUTH_REDIRECT_STARTED_AT_KEY, String(authRedirectStartedAt));

    const originalUri = toRelativeUrl(
      window.location.href,
      window.location.origin,
    );
    oktaAuth.setOriginalUri(originalUri);
    oktaAuth.signInWithRedirect();
  } catch (error) {
    clearStaleRedirectState();
    throw error;
  }

  return { isAuthenticated: false, isLoading: true };
};
